// 聊天摘要生成服务 - 为长期记忆系统生成对话摘要
import { RealAIService } from './realAiService.js'
import settingsService from './settingsService.js'
import { v4 as uuidv4 } from 'uuid'

class ChatSummaryService {
  /**
   * 生成对话摘要
   * @param {Object} character - 角色信息
   * @param {Array} messages - 对话消息列表
   * @param {string} sessionId - 会话ID
   * @returns {Object} 摘要对象
   */
  async generateSummary(character, messages, sessionId) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        return this.generateMockSummary(character, messages, sessionId)
      }

      // 构建对话内容
      const conversationText = messages
        .map(msg => `${msg.sender === 'user' ? '用户' : character.name}：${msg.content}`)
        .join('\n')

      // 提取关键词（简单实现，后续可优化）
      const keywords = this.extractKeywords(messages)
      
      // 分析情感倾向
      const emotions = this.analyzeEmotions(messages)

      // 使用AI生成摘要
      const prompt = `请为以下对话生成一个简洁的摘要（100-200字），要求：
1. 总结对话的主要话题和内容
2. 记录重要的信息、事件或决定
3. 描述对话的整体氛围和情感倾向
4. 使用第三人称描述，如"用户和${character.name}聊了..."

对话内容：
${conversationText}

请生成摘要：`

      const summary = await RealAIService.callLanguageModel(prompt)

      // 提取重要事件（简单实现）
      const importantEvents = this.extractImportantEvents(messages, summary)

      return {
        id: uuidv4(),
        characterId: character.id,
        sessionId: sessionId,
        summary: summary.trim(),
        keywords: keywords,
        emotions: emotions,
        importantEvents: importantEvents,
        timestamp: Date.now(),
        messageCount: messages.length
      }
    } catch (error) {
      console.error('❌ 生成对话摘要失败:', error)
      return this.generateMockSummary(character, messages, sessionId)
    }
  }

  /**
   * 提取关键词
   */
  extractKeywords(messages) {
    const keywords = []
    const commonWords = ['的', '了', '是', '在', '我', '你', '他', '她', '这', '那', '有', '和', '就', '不', '也', '都', '要', '会', '可以', '吗', '呢', '啊']
    
    // 简单的关键词提取（后续可以优化为使用AI）
    const allText = messages.map(m => m.content).join(' ')
    const words = allText.split(/[\s，。！？、；：]+/)
    
    words.forEach(word => {
      if (word.length >= 2 && !commonWords.includes(word)) {
        if (!keywords.includes(word)) {
          keywords.push(word)
        }
      }
    })

    return keywords.slice(0, 10) // 最多10个关键词
  }

  /**
   * 分析情感倾向
   */
  analyzeEmotions(messages) {
    const positiveKeywords = ['喜欢', '爱', '开心', '高兴', '谢谢', '感谢', '棒', '好', '不错', '太好了']
    const negativeKeywords = ['讨厌', '烦', '无聊', '不好', '差', '生气', '难过', '失望']
    
    let positive = 0
    let negative = 0
    let neutral = 0

    messages.forEach(msg => {
      const content = msg.content.toLowerCase()
      const positiveCount = positiveKeywords.filter(k => content.includes(k)).length
      const negativeCount = negativeKeywords.filter(k => content.includes(k)).length
      
      if (positiveCount > negativeCount) {
        positive++
      } else if (negativeCount > positiveCount) {
        negative++
      } else {
        neutral++
      }
    })

    return {
      positive,
      negative,
      neutral,
      total: messages.length
    }
  }

  /**
   * 提取重要事件
   */
  extractImportantEvents(messages, summary) {
    const events = []
    
    // 简单实现：检测礼物、约定、重要决定等
    messages.forEach(msg => {
      const content = msg.content.toLowerCase()
      if (content.includes('礼物') || content.includes('送')) {
        events.push('赠送礼物')
      }
      if (content.includes('约定') || content.includes('约定')) {
        events.push('做出约定')
      }
      if (content.includes('喜欢') || content.includes('爱')) {
        events.push('表达情感')
      }
    })

    return [...new Set(events)] // 去重
  }

  /**
   * 生成模拟摘要（降级方案）
   */
  generateMockSummary(character, messages, sessionId) {
    return {
      id: uuidv4(),
      characterId: character.id,
      sessionId: sessionId,
      summary: `用户和${character.name}进行了${messages.length}条消息的对话，话题涉及日常聊天。`,
      keywords: ['聊天', '对话'],
      emotions: {
        positive: Math.floor(messages.length * 0.6),
        negative: Math.floor(messages.length * 0.1),
        neutral: Math.floor(messages.length * 0.3),
        total: messages.length
      },
      importantEvents: [],
      timestamp: Date.now(),
      messageCount: messages.length
    }
  }
}

export default new ChatSummaryService()

